import { useMutation, useQuery } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';

export type AuditLogCategory =
  | 'authentication'
  | 'data_access'
  | 'data_modification'
  | 'cpr_access'
  | 'export'
  | 'configuration'
  | 'security';

export type AuditLogAction =
  // Authentication
  | 'login'
  | 'logout'
  | 'login_failed'
  | 'password_reset_requested'
  | 'password_changed'
  | 'mfa_enabled'
  | 'mfa_disabled'
  // Data Access
  | 'patient_viewed'
  | 'patient_list_viewed'
  | 'cpr_revealed'
  | 'appointment_viewed'
  | 'invoice_viewed'
  | 'journal_viewed'
  | 'document_viewed'
  | 'document_downloaded'
  // Data Modification
  | 'patient_created'
  | 'patient_updated'
  | 'patient_archived'
  | 'patient_restored'
  | 'appointment_created'
  | 'appointment_updated'
  | 'appointment_cancelled'
  | 'appointment_completed'
  | 'invoice_created'
  | 'invoice_updated'
  | 'invoice_sent'
  | 'invoice_paid'
  | 'journal_created'
  | 'journal_updated'
  | 'journal_deleted'
  | 'document_uploaded'
  | 'document_deleted'
  // Export
  | 'patient_data_exported'
  | 'invoice_data_exported'
  | 'appointment_data_exported'
  | 'bulk_export'
  // Configuration
  | 'clinic_settings_changed'
  | 'user_invited'
  | 'user_removed'
  | 'permission_changed'
  // Security
  | 'unauthorized_access_attempt'
  | 'suspicious_activity'
  | 'data_breach_detected';

export interface AuditLogRow {
  id: string;
  clinic_id: string;
  category: AuditLogCategory;
  action: AuditLogAction;
  actor_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  actor_ip_address: string | null;
  actor_user_agent: string | null;
  target_type: string;
  target_id: string | null;
  target_identifier: string | null;
  source: string;
  source_function: string | null;
  purpose: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  retention_days: number;
  delete_after: string | null;
  request_id: string | null;
  session_id: string | null;
  created_at: string;
}

export interface AuditLogFilters {
  start_date?: string;
  end_date?: string;
  category?: AuditLogCategory;
  action?: AuditLogAction;
  actor_id?: string;
  target_type?: string;
  target_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface QueryAuditLogsInput extends AuditLogFilters {
  clinic_id: string;
}

export interface QueryAuditLogsOutput {
  logs: AuditLogRow[];
  total_count: number;
  has_more: boolean;
}

export interface LogAuditEventInput {
  clinic_id: string;
  category: AuditLogCategory;
  action: string;
  target_type: string;
  target_id?: string;
  target_identifier?: string;
  source?: string;
  source_function?: string;
  purpose?: string;
  metadata?: Record<string, unknown>;
  actor_ip_address?: string;
  actor_user_agent?: string;
  session_id?: string;
  retention_days?: number;
}

export interface LogAuditEventOutput {
  audit_id: string;
  created_at: string;
}

export const auditLogsQueryKey = (clinicId: string | null, filters: AuditLogFilters) =>
  ['audit-logs', clinicId, filters] as const;

/**
 * Query audit logs for a clinic.
 * Requires clinic admin permissions.
 */
export function useAuditLogs(clinicId: string | null | undefined, filters: AuditLogFilters = {}) {
  return useQuery({
    queryKey: auditLogsQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<QueryAuditLogsOutput> => {
      if (!clinicId) throw new Error('No clinic id');
      return await callFunction<Record<string, unknown>, QueryAuditLogsOutput>('query-audit-logs', {
        clinic_id: clinicId,
        ...filters,
      } as Record<string, unknown>);
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Log an audit event.
 * Called when user performs an action that should be audited
 * (e.g., views sensitive data, exports data).
 */
export function useLogAuditEvent() {
  return useMutation({
    mutationFn: async (input: LogAuditEventInput): Promise<LogAuditEventOutput> => {
      return await callFunction<Record<string, unknown>, LogAuditEventOutput>('log-audit-event', input as unknown as Record<string, unknown>);
    },
  });
}

/**
 * Helper to log patient data access.
 * Call this whenever a user views patient details.
 */
export function useLogPatientAccess() {
  const logEvent = useLogAuditEvent();

  return {
    logPatientViewed: (clinicId: string, patientId: string, patientName: string) => {
      return logEvent.mutateAsync({
        clinic_id: clinicId,
        category: 'data_access',
        action: 'patient_viewed',
        target_type: 'patient',
        target_id: patientId,
        target_identifier: patientName,
        source: 'web',
      });
    },
    logCprRevealed: (clinicId: string, patientId: string, patientName: string, purpose?: string) => {
      return logEvent.mutateAsync({
        clinic_id: clinicId,
        category: 'cpr_access',
        action: 'cpr_revealed',
        target_type: 'patient',
        target_id: patientId,
        target_identifier: patientName,
        purpose,
        source: 'web',
        retention_days: 2555, // 7 years for CPR access
      });
    },
    logDocumentDownloaded: (
      clinicId: string,
      documentId: string,
      fileName: string,
      patientId: string
    ) => {
      return logEvent.mutateAsync({
        clinic_id: clinicId,
        category: 'data_access',
        action: 'document_downloaded',
        target_type: 'document',
        target_id: documentId,
        target_identifier: fileName,
        metadata: { patient_id: patientId },
        source: 'web',
      });
    },
  };
}

/**
 * Helper to log data exports.
 * Call this when user exports patient data, invoices, etc.
 */
export function useLogDataExport() {
  const logEvent = useLogAuditEvent();

  return {
    logExport: (
      clinicId: string,
      exportType: 'patient_data' | 'invoice_data' | 'appointment_data' | 'bulk',
      metadata: Record<string, unknown> = {}
    ) => {
      const actionMap = {
        patient_data: 'patient_data_exported',
        invoice_data: 'invoice_data_exported',
        appointment_data: 'appointment_data_exported',
        bulk: 'bulk_export',
      };

      return logEvent.mutateAsync({
        clinic_id: clinicId,
        category: 'export',
        action: actionMap[exportType],
        target_type: 'export',
        metadata,
        source: 'web',
        retention_days: 2555, // 7 years for export logs
      });
    },
  };
}
